import React, { useState, useEffect, lazy } from 'react';

import { Shield, AlertTriangle, Activity, Lock, Eye, FileWarning, TrendingUp, CheckCircle } from 'lucide-react';
import { UnifiedHeader } from '../../components/unified';
import securityMonitoringService from '../../services/securityMonitoringService';

import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';


const SecurityMonitoringDashboard = () => {
  const [alerts, setAlerts] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [securityReport, setSecurityReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [recentEvents, setRecentEvents] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  useEffect(() => {
    loadSecurityData();
    startMonitoring();

    return () => {
      securityMonitoringService?.stopMonitoring();
    };
  }, []);

  const loadSecurityData = async () => {
    try {
      setLoading(true);

      // Load alerts
      const recentAlerts = securityMonitoringService?.getRecentAlerts(20);
      setAlerts(recentAlerts);

      // Load metrics
      const currentMetrics = securityMonitoringService?.getMetrics();
      setMetrics(currentMetrics);

      // Scan vulnerabilities
      const vulns = await securityMonitoringService?.scanVulnerabilities();
      setVulnerabilities(vulns);

      // Generate security report
      const report = await securityMonitoringService?.generateSecurityReport();
      setSecurityReport(report);

      // Load recent security events
      await loadRecentEvents();

    } catch (error) {
      console.error('Failed to load security data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentEvents = async () => {
    try {
      const { data, error } = await supabase?.from('security_audit_log')?.select('*')?.order('timestamp', { ascending: false })?.limit(50);

      if (!error && data) {
        setRecentEvents(data);
      }
    } catch (error) {
      console.error('Failed to load recent events:', error);
    }
  };

  const startMonitoring = () => {
    securityMonitoringService?.startMonitoring();

    // Refresh data every 30 seconds
    const interval = setInterval(() => {
      loadSecurityData();
    }, 30000);

    return () => clearInterval(interval);
  };

  const handleAcknowledgeAlert = (alertId) => {
    securityMonitoringService?.acknowledgeAlert(alertId);
    setAlerts(alerts?.map(a => 
      a?.id === alertId ? { ...a, acknowledged: true } : a
    ));
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case 'high': return <FileWarning className="w-5 h-5 text-orange-600" />;
      case 'medium': return <Eye className="w-5 h-5 text-yellow-600" />;
      case 'low': return <Activity className="w-5 h-5 text-blue-600" />;
      default: return <Shield className="w-5 h-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <UnifiedHeader title="Security Monitoring" onToggleSidebar={handleToggleSidebar} />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading security data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <UnifiedHeader title="Security Monitoring Dashboard" onToggleSidebar={handleToggleSidebar} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Security Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <Shield className="w-8 h-8 text-green-600" />
              <span className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">
                Active
              </span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {securityReport?.vulnerabilities?.length || 0}
            </h3>
            <p className="text-sm text-gray-600">Vulnerabilities Detected</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <span className="text-sm font-medium text-red-600 bg-red-50 px-3 py-1 rounded-full">
                {securityReport?.alerts?.critical || 0}
              </span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {alerts?.length}
            </h3>
            <p className="text-sm text-gray-600">Active Alerts</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <Activity className="w-8 h-8 text-blue-600" />
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {metrics?.authAttempts || 0}
            </h3>
            <p className="text-sm text-gray-600">Auth Attempts (24h)</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <Lock className="w-8 h-8 text-purple-600" />
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {metrics?.blockedRequests || 0}
            </h3>
            <p className="text-sm text-gray-600">Blocked Requests</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {[
                { id: 'overview', label: 'Overview', icon: Shield },
                { id: 'alerts', label: 'Alerts', icon: AlertTriangle },
                { id: 'events', label: 'Security Events', icon: Activity },
                { id: 'vulnerabilities', label: 'Vulnerabilities', icon: FileWarning }
              ]?.map((tab) => {
                const Icon = tab?.icon;
                return (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                      flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm
                      ${activeTab === tab?.id
                        ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className="w-4 h-4" />
                    {tab?.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Security Metrics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Failed Logins</p>
                      <p className="text-2xl font-bold text-gray-900">{metrics?.failedLogins || 0}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">XSS Attempts</p>
                      <p className="text-2xl font-bold text-gray-900">{metrics?.xssAttempts || 0}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">SQL Injection Attempts</p>
                      <p className="text-2xl font-bold text-gray-900">{metrics?.sqlInjectionAttempts || 0}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Suspicious Activity</p>
                      <p className="text-2xl font-bold text-gray-900">{metrics?.suspiciousActivity || 0}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Blocked Requests</p>
                      <p className="text-2xl font-bold text-gray-900">{metrics?.blockedRequests || 0}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Auth Attempts</p>
                      <p className="text-2xl font-bold text-gray-900">{metrics?.authAttempts || 0}</p>
                    </div>
                  </div>
                </div>

                {securityReport?.recommendations && securityReport?.recommendations?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Security Recommendations</h3>
                    <div className="space-y-3">
                      {securityReport?.recommendations?.map((rec, index) => (
                        <div key={index} className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-blue-900">{rec?.action}</p>
                            <p className="text-sm text-blue-700 mt-1">Priority: {rec?.priority}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Alerts Tab */}
            {activeTab === 'alerts' && (
              <div className="space-y-4">
                {alerts?.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                    <p className="text-gray-600">No active security alerts</p>
                  </div>
                ) : (
                  alerts?.map((alert) => (
                    <div
                      key={alert?.id}
                      className={`p-4 rounded-lg border ${getSeverityColor(alert?.severity)}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          {getSeverityIcon(alert?.severity)}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold">{alert?.message}</h4>
                              <span className="text-xs px-2 py-1 rounded-full bg-white border">
                                {alert?.severity}
                              </span>
                            </div>
                            <p className="text-sm opacity-75 mb-2">
                              {new Date(alert.timestamp)?.toLocaleString()}
                            </p>
                            {alert?.details && Object.keys(alert?.details)?.length > 0 && (
                              <div className="text-sm opacity-75">
                                <pre className="whitespace-pre-wrap">
                                  {JSON.stringify(alert?.details, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                        {!alert?.acknowledged && (
                          <button
                            onClick={() => handleAcknowledgeAlert(alert?.id)}
                            className="ml-4 px-3 py-1 text-sm font-medium bg-white border rounded-lg hover:bg-gray-50"
                          >
                            Acknowledge
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Security Events Tab */}
            {activeTab === 'events' && (
              <div className="space-y-4">
                {recentEvents?.length === 0 ? (
                  <div className="text-center py-12">
                    <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No security events recorded</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Event Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Timestamp
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Severity
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Details
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {recentEvents?.map((event) => (
                          <tr key={event?.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {event?.event_type}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(event.timestamp)?.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(event?.severity || 'low')}`}>
                                {event?.severity || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {event?.ip_address && (
                                <span className="mr-2">IP: {event?.ip_address}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Vulnerabilities Tab */}
            {activeTab === 'vulnerabilities' && (
              <div className="space-y-4">
                {vulnerabilities?.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                    <p className="text-gray-600">No vulnerabilities detected</p>
                  </div>
                ) : (
                  vulnerabilities?.map((vuln, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border ${getSeverityColor(vuln?.severity)}`}
                    >
                      <div className="flex items-start gap-3">
                        {getSeverityIcon(vuln?.severity)}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold">{vuln?.type}</h4>
                            <span className="text-xs px-2 py-1 rounded-full bg-white border">
                              {vuln?.severity}
                            </span>
                          </div>
                          <p className="text-sm opacity-75 mb-2">{vuln?.description}</p>
                          {vuln?.count && (
                            <p className="text-sm opacity-75">Affected items: {vuln?.count}</p>
                          )}
                          {vuln?.missing && (
                            <div className="mt-2">
                              <p className="text-sm font-medium mb-1">Missing:</p>
                              <ul className="text-sm opacity-75 list-disc list-inside">
                                {vuln?.missing?.map((item, i) => (
                                  <li key={i}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityMonitoringDashboard;